import React, {useState, useEffect} from "react";
import {
    Box,
    Paper,
    Typography,
    Avatar,
    Grid,
    Tab,
    Tabs,
    Skeleton,
    Alert,
    useMediaQuery,
    useTheme,
} from "@mui/material";


import {
    Home as HomeIcon,
    DocumentScanner as DocumentIcon,
    Badge as BadgeIcon,
} from "@mui/icons-material";
import {KEY} from "../../dummyData";
import CryptoJS from "crypto-js";
import {useFeatureFlags} from "../../context/FeatureFlagsContext";
import DisabledFeatureBanner from "../../components/FeatureGate/DisabledFeatureBanner";
import {Person} from "@material-ui/icons";
import PersonalInfoTab from "./components/Personalinfo";
import Documents from "./components/Documents";
import LeaseDetails from "./components/LeaseDetails";
import IDUploadPage from "../IDupload/IDUploadPage";
import {useLocation} from "react-router-dom";

// Sample interface for types
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// Tab Panel component
function TabPanel(props: TabPanelProps) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`profile-tabpanel-${index}`}
            aria-labelledby={`profile-tab-${index}`}
            {...other}
            style={{padding: '24px 0'}}
        >
            {value === index && children}
        </div>
    );
}

export default function ProfilePage() {
    const { isEnabled, getMessage } = useFeatureFlags();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();

    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );

    let originalText = details.toString(CryptoJS.enc.Utf8);
    let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));

    // Get initial tab from navigation state (if provided)
    const initialTab = location.state?.tab || 0;

    // State variables
    const [tenantID, setTenantID] = useState(tenantDetails.get("tenant_id"));
    const [tabValue, setTabValue] = useState(initialTab);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        property: "",
        unit: "",
        profileImage: null
    });

    // Load user profile data from the already-decrypted tenant_details in localStorage
    useEffect(() => {
        try {
            let tenantDetails = new Map(Object.entries(JSON.parse(originalText)));

            setTenantID(tenantDetails.get("tenant_id"));

            setProfileData({
                firstName: tenantDetails.get("first_name") || "",
                lastName: tenantDetails.get("last_name") || "",
                property: tenantDetails.get("property_name") || "",
                unit: tenantDetails.get("house_number") || "",
                profileImage: null
            });

            setLoading(false);
        } catch (error) {
            console.error("Error loading profile data:", error);
            setNotification({
                type: "error",
                message: "Failed to load profile data. Please try again later."
            });
            setLoading(false);
        }
    }, []);

    // Tab change handler
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Get avatar initials
    const getInitials = () => {
        if (!profileData.firstName && !profileData.lastName) return "";

        const firstInitial = profileData.firstName ? profileData.firstName.charAt(0) : "";
        const lastInitial = profileData.lastName ? profileData.lastName.charAt(0) : "";

        return (firstInitial + lastInitial).toUpperCase();
    };

    if (!isEnabled('profile')) {
        return <DisabledFeatureBanner featureName="My Profile" message={getMessage('profile')} />;
    }

    return (
        <Box sx={{
            backgroundColor: '#f9fafb',
            minHeight: '100vh'
        }}>
            {/* Notification Alert */}
            {notification && (
                <Alert
                    severity={notification.type}
                    sx={{mb: 3, borderRadius: '8px', mx: 2, mt: 2}}
                    onClose={() => setNotification(null)}
                >
                    {notification.message}
                </Alert>
            )}

            {/* Profile Header */}
            <Paper
                elevation={0}
                sx={{
                    p: {xs: 2, sm: 3},
                    mb: 3,
                    mx: 2,
                    mt: 2,
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'white'
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    {/* Profile Image */}
                    <Grid item xs={12} sm="auto" sx={{textAlign: {xs: 'center', sm: 'left'}}}>
                        {loading ? (
                            <Skeleton variant="circular" width={100} height={100}/>
                        ) : (
                            <Avatar
                                src={profileData.profileImage}
                                sx={{
                                    width: 100,
                                    height: 100,
                                    fontSize: '2.5rem',
                                    fontWeight: 'bold',
                                    bgcolor: '#3B82F6',
                                    border: '4px solid #EBF4FF'
                                }}
                            >
                                {getInitials()}
                            </Avatar>
                        )}
                    </Grid>

                    {/* Profile Info */}
                    <Grid item xs={12} sm>
                        <Box sx={{textAlign: {xs: 'center', sm: 'left'}}}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width={200} height={40}/>
                                    <Skeleton variant="text" width={250} height={24}/>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1F2937',
                                            lineHeight: 1.2,
                                            mb: 0.5
                                        }}
                                    >
                                        {profileData.firstName} {profileData.lastName}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#6B7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: {xs: 'center', sm: 'flex-start'},
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        <HomeIcon sx={{fontSize: '1.1rem', mr: 0.5, color: '#3B82F6'}}/>
                                        {profileData.property} • Unit {profileData.unit}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Profile Tabs Navigation */}
            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2, mx: 2}}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    allowScrollButtonsMobile
                    aria-label="Profile information tabs"
                    sx={{
                        '& .MuiTabs-flexContainer': {
                            borderBottom: '1px solid #E5E7EB',
                        },
                        backgroundColor: 'white',
                        borderRadius: '8px 8px 0 0',
                        '& .MuiTab-root': {
                            backgroundColor: 'white'
                        }
                    }}
                >
                    <Tab
                        label="Personal Info"
                        id="profile-tab-0"
                        aria-controls="profile-tabpanel-0"
                        icon={<Person fontSize="small"/>}
                        iconPosition="start"
                        className="personal-info-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#3B82F6',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />

                    <Tab
                        label="Lease Details"
                        id="profile-tab-1"
                        aria-controls="profile-tabpanel-1"
                        icon={<HomeIcon fontSize="small"/>}
                        iconPosition="start"
                        className="lease-details-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#10B981',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />

                    <Tab
                        label="Documents"
                        id="profile-tab-2"
                        aria-controls="profile-tabpanel-2"
                        icon={<DocumentIcon fontSize="small"/>}
                        iconPosition="start"
                        className="documents-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#EC4899',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />

                    <Tab
                        label="ID Verification"
                        id="profile-tab-3"
                        aria-controls="profile-tabpanel-3"
                        icon={<BadgeIcon fontSize="small"/>}
                        iconPosition="start"
                        className="id-verification-tab"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: {xs: 2, md: 3},
                            py: 1.5,
                            color: '#6B7280',
                            '&.Mui-selected': {
                                color: '#F59E0B',
                                fontWeight: 600
                            },
                            '& .MuiTab-iconWrapper': {
                                mr: 1,
                                mb: 0
                            }
                        }}
                    />
                </Tabs>
            </Box>

            {/* Tab Content Container */}
            <Box sx={{
                backgroundColor: 'white',
                borderRadius: '0 0 12px 12px',
                minHeight: '400px',
                mx: 2
            }}>
                {/* Personal Info Tab */}
                <TabPanel value={tabValue} index={0}>
                    {isEnabled('profile_personal_info') ? (
                        <PersonalInfoTab
                            className="personal-info-content"
                            onUpdate={(updatedData) => {
                                console.log('Personal info updated:', updatedData);
                            }}
                        />
                    ) : (
                        <DisabledFeatureBanner featureName="Personal Info" message={getMessage('profile_personal_info')} />
                    )}
                </TabPanel>

                {/* Lease Tab */}
                <TabPanel value={tabValue} index={1} className="lease-details-tab-panel">
                    {isEnabled('profile_lease_details') ? (
                        <LeaseDetails tenantId={tenantID} />
                    ) : (
                        <DisabledFeatureBanner featureName="Lease Details" message={getMessage('profile_lease_details')} />
                    )}
                </TabPanel>

                {/* Documents Tab */}
                <TabPanel value={tabValue} index={2}>
                    {isEnabled('profile_documents') ? (
                        <Documents tenantId={tenantID} />
                    ) : (
                        <DisabledFeatureBanner featureName="Documents" message={getMessage('profile_documents')} />
                    )}
                </TabPanel>

                {/* ID Verification Tab */}
                <TabPanel value={tabValue} index={3}>
                    {isEnabled('profile_id_verification') ? (
                        <IDUploadPage />
                    ) : (
                        <DisabledFeatureBanner featureName="ID Verification" message={getMessage('profile_id_verification')} />
                    )}
                </TabPanel>
            </Box>
        </Box>
    );
};